import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { combineLatest, Observable } from "rxjs";
import { firstValueFrom } from "rxjs";
import { filter, map, shareReplay, switchMap } from "rxjs/operators";
import {
    AccountSettingsResource,
    AttachmentSendType,
    OrganizationSettingsResource,
    RequireTwoFactorResource,
    RequireWhitelistedIpResource,
    UpsertEntitySettingsRequestResource
} from "../models";
import { InteroperabilitySettingsResource } from "../models/interoperability-settings-resource.model";
import { SettingsEntityType } from "../models/settings-entity-type.model";
import { ProfileService } from "../profile/profile.service";

@Injectable({ providedIn: "root" })
export class SettingsService {
    private httpClient = inject(HttpClient);
    private profileService = inject(ProfileService);

    private settings$ = this.httpClient.get<OrganizationSettingsResource>("/api/settings/").pipe(shareReplay(1));

    private interoperabilitySettings$ = this.httpClient.get<InteroperabilitySettingsResource>("/api/settings/interoperability").pipe(shareReplay(1));

    public getSettings$(): Observable<OrganizationSettingsResource> {
        return this.settings$;
    }

    public getInteroperabilitySettings$(): Observable<InteroperabilitySettingsResource> {
        return this.interoperabilitySettings$;
    }

    public getEntitySettings$(entityType: SettingsEntityType, entityId: string): Observable<Record<any, any>> {
        return this.httpClient.get<Record<any, any>>(`/api/settings/${entityType}/${entityId}`).pipe(shareReplay(1));
    }

    public getBrandSettings$(): Observable<Record<string, boolean>> {
        return this.profileService.getProfileData$().pipe(
            switchMap((data: any) => this.getEntitySettings$(SettingsEntityType.Brand, data?.brandId)),
            map((data) => data?.data)
        );
    }

    public areVisitDetailsReadOnly$(): Observable<boolean> {
        return this.interoperabilitySettings$.pipe(
            filter((settings) => !!settings),
            map((settings: InteroperabilitySettingsResource) => {
                const { exportMode, exportDirectoryType, exportOption, exportVisitDirectory } = settings ?? {};
                const isSensitive = exportMode === "print" || exportDirectoryType === "single_directory" || exportOption === "xds-sd_cda";
                const isSaveAsSensitive =
                    exportMode === "save_as" && exportDirectoryType === "patient_folder" && exportOption === "standard" && !exportVisitDirectory;

                return isSensitive || isSaveAsSensitive;
            })
        );
    }

    public canForwardToEmr$(): Observable<boolean> {
        return this.interoperabilitySettings$.pipe(map((data) => data?.exportMode === "emr" && !!data?.directAddress));
    }

    public canDownloadToEmr$(): Observable<boolean> {
        return this.interoperabilitySettings$.pipe(map((data) => data?.downloadMessage));
    }

    public exportOptions$(): Observable<any> {
        return combineLatest([this.interoperabilitySettings$, this.getBrandSettings$()]).pipe(
            filter(([interopSettings]) => !!interopSettings),
            map(([interopSettings, brandSettings]) => ({ ...interopSettings, ...brandSettings })),
            map((result: any) => {
                const showExportDropdown = result.exportMode !== "emr" && (result.brandShowDownloadWithClient || result.brandShowDownloadWithDesktop);
                return {
                    exportMode: result.exportMode,
                    enablePrintFromBrowser: result.enablePrintFromBrowser && showExportDropdown,
                    enableDownloadFromBrowser: result.enableDownloadFromBrowser && showExportDropdown,
                    brandShowDownloadWithClient: result.brandShowDownloadWithClient,
                    brandShowDownloadWithDesktop: result.brandShowDownloadWithDesktop,
                    fromDirectAddress: result.fromDirectAddress,
                    forwardOption: <AttachmentSendType>result.forwardOption
                };
            })
        );
    }

    public directAddresses$(): Observable<Array<string>> {
        return this.interoperabilitySettings$.pipe(map((data) => data?.directAddress?.split(";") || []));
    }

    public getInteroperabilitySetting$(setting: keyof InteroperabilitySettingsResource): Observable<boolean | string> {
        return this.interoperabilitySettings$.pipe(
            filter((settings) => !!settings),
            map((settings) => settings[setting])
        );
    }

    public getOrganizationInteroperabilitySettings$(): Observable<InteroperabilitySettingsResource> {
        return this.interoperabilitySettings$.pipe(filter((settings) => !!settings));
    }

    public updateSessionTimeout(minutes: number): Promise<AccountSettingsResource> {
        return firstValueFrom(this.httpClient.put<AccountSettingsResource>("/api/settings/sessiontimeout", { sessionTimeoutMinutes: minutes }));
    }

    public updateRequireWhitelistedIp(requireIp: boolean): Promise<RequireWhitelistedIpResource> {
        return firstValueFrom(this.httpClient.put<RequireWhitelistedIpResource>("/api/settings/requirewhitelistedip", { requireWhitelistedIp: requireIp }));
    }

    public updateIgnoredExtensions(ignoredExtensions: Array<string>): Promise<void> {
        return firstValueFrom(this.httpClient.put<void>("/api/settings/ignoredattachmentextensions", { ignoredExtensions: ignoredExtensions }));
    }

    public updateRequire2FA(require2FA: boolean): Promise<RequireTwoFactorResource> {
        return firstValueFrom(this.httpClient.put<RequireTwoFactorResource>("/api/settings/requiretwofactorauth", { requireTwoFactorAuth: require2FA }));
    }

    public updateAllowLegacyLogin(allowLegacy: boolean): Promise<void> {
        return firstValueFrom(this.httpClient.put<void>("/api/settings/allowcompatibility", { allowOrganizationLegacyLoginCompatibility: allowLegacy }));
    }

    public updateInteropSettings(interopSettings: InteroperabilitySettingsResource): Promise<InteroperabilitySettingsResource> {
        return firstValueFrom(this.httpClient.put<InteroperabilitySettingsResource>("/api/settings/interoperability", interopSettings));
    }

    public updateEntitySettings(settings: UpsertEntitySettingsRequestResource): Promise<any> {
        return firstValueFrom(this.httpClient.post<any>("/api/settings", settings));
    }
}
